import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import { CheckCircle2, X } from "lucide-react";

const CAT_EMOJI = Object.fromEntries(CATEGORIES.map(c => [c.value, c.emoji]));

function timeAgo(iso) {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 5) return "lige nu";
  if (mins < 60) return `for ${mins} min. siden`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `for ${hours} time${hours > 1 ? "r" : ""} siden`;
  const days = Math.floor(hours / 24);
  return `for ${days} dag${days > 1 ? "e" : ""} siden`;
}

export default function LiveActivity() {
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const indexRef = useRef(0);

  useEffect(() => {
    let mounted = true;
    api.get("/activity").then(r => { if (mounted) setItems(r.data || []); }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (dismissed || items.length === 0) return;
    // initial show after small delay
    const initial = setTimeout(() => {
      setCurrent(items[0]);
      indexRef.current = 1;
    }, 1500);

    const cycle = setInterval(() => {
      setCurrent(null);
      if (indexRef.current >= items.length) {
        clearInterval(cycle);
        return;
      }
      setTimeout(() => {
        const next = items[indexRef.current];
        setCurrent({ ...next, _key: Date.now() });
        indexRef.current += 1;
      }, 450); // gap between exit and entry
    }, 5500);

    return () => { clearTimeout(initial); clearInterval(cycle); };
  }, [items, dismissed]);

  if (dismissed) return null;

  return (
    <div className="fixed left-4 bottom-20 md:bottom-6 z-30 pointer-events-none">
      <AnimatePresence mode="wait">
        {current && (
          <motion.div
            key={current._key || current.first_name}
            initial={{ opacity: 0, x: -30, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: -30, y: 10, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 240, damping: 22 }}
            className="pointer-events-auto max-w-[280px] sm:max-w-[320px]"
            data-testid="live-activity-toast"
          >
            <div className="glass rounded-2xl px-3 py-2.5 shadow-xl shadow-foreground/10 border border-border/60 flex items-center gap-3 relative">
              <button
                onClick={() => setDismissed(true)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-background border border-border rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground shadow-sm"
                aria-label="Luk"
                data-testid="live-activity-close"
              >
                <X className="w-3 h-3" />
              </button>
              <div className="relative shrink-0">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center text-lg">
                  {CAT_EMOJI[current.category] || "📦"}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-primary rounded-full flex items-center justify-center border-2 border-background">
                  <CheckCircle2 className="w-2.5 h-2.5 text-primary-foreground" strokeWidth={3} />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs leading-tight">
                  <span className="font-semibold text-foreground">{current.first_name}</span>
                  <span className="text-muted-foreground"> i </span>
                  <span className="font-medium text-foreground">{current.city}</span>
                </p>
                <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                  {current.title}
                </p>
                <p className="text-[11px] font-semibold text-primary mt-0.5">
                  {current.amount} kr. <span className="text-muted-foreground font-normal">· {timeAgo(current.when)}</span>
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
